import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { PageHeader } from '../components/PageHeader';
import { useRegion } from '../hooks/useRegion';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Illustration } from '../components/Illustration';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDate } from '../lib/utils';
import { isCompletedJob, isCurrentJob, isLostJob } from '../lib/jobStatus';
import { Plus, Search, Briefcase, Loader2, CheckCircle, XCircle, Pencil, Check, X } from 'lucide-react';
import JobCalendarEnhanced from '../components/JobCalendarEnhanced';
import { JobKanban } from '../components/JobKanban';
import { CompletedJobsBreakdown } from '../components/CompletedJobsBreakdown';
import { PageToolbar } from '../components/PageToolbar';
import { PhonePanel } from '../components/PhonePanel';
import { useIsPhone } from '../hooks/useIsPhone';

export default function JobsPage() {
  const { jobs, fetchJobs, jobsLoading, updateJob, briefs, fetchBriefs, treatments, fetchTreatments } = useAppStore();
  const { hasProjectsPlan, isAdmin, isDemo } = useAuthStore();
  // Briefs and treatments are Projects-plan features; the job cards only show
  // their papers to accounts that can open them.
  const canSeeProjectDocs = hasProjectsPlan || isAdmin || isDemo;
  const { formatCurrency, region, getTaxName } = useRegion();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const isPhone = useIsPhone();
  
  // State for inline editing of lost reason
  const [editingLostReasonId, setEditingLostReasonId] = useState(null);
  const [editingLostReasonValue, setEditingLostReasonValue] = useState('');

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (!canSeeProjectDocs) return;
    fetchBriefs();
    fetchTreatments();
  }, [canSeeProjectDocs, fetchBriefs, fetchTreatments]);

  const briefsById = useMemo(
    () => Object.fromEntries((canSeeProjectDocs ? briefs : []).map((b) => [b.id, b])),
    [briefs, canSeeProjectDocs]
  );
  // A treatment holds the link to its job; newest first, as on the job page
  const treatmentsByJob = useMemo(() => {
    const map = {};
    (canSeeProjectDocs ? treatments : []).forEach((t) => {
      if (!t.job_id) return;
      (map[t.job_id] = map[t.job_id] || []).push(t);
    });
    return map;
  }, [treatments, canSeeProjectDocs]);

  // Split into active, lost, and completed jobs (rules in lib/jobStatus)
  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      job.project_name?.toLowerCase().includes(search.toLowerCase()) ||
      job.client?.toLowerCase().includes(search.toLowerCase()) ||
      job.client_job_number?.toLowerCase().includes(search.toLowerCase());

    // Filter by active tab
    if (activeTab === 'active' && !isCurrentJob(job)) return false;
    if (activeTab === 'lost' && !isLostJob(job)) return false;
    if (activeTab === 'completed' && !isCompletedJob(job)) return false;

    return matchesSearch;
  });

  // Count for tabs (before search/status filter, but respecting tab selection)
  const activeCount = jobs.filter(isCurrentJob).length;
  const lostCount = jobs.filter(isLostJob).length;
  const completedCount = jobs.filter(isCompletedJob).length;

  // Handle saving lost reason inline
  const handleSaveLostReason = async (jobId) => {
    try {
      await updateJob(jobId, { lost_reason: editingLostReasonValue });
      setEditingLostReasonId(null);
      setEditingLostReasonValue('');
    } catch (error) {
      console.error('Failed to update lost reason:', error);
    }
  };

  // Start editing lost reason
  const startEditingLostReason = (jobId, currentReason) => {
    setEditingLostReasonId(jobId);
    setEditingLostReasonValue(currentReason || '');
  };

  // Cancel editing
  const cancelEditingLostReason = () => {
    setEditingLostReasonId(null);
    setEditingLostReasonValue('');
  };

  return (
    <Layout>
      <div className="space-y-8 animate-in" data-testid="jobs-page">
        <PageHeader title="Jobs" />

        {/* Tabs, Filters & New Job Button - All Inline */}
        {isPhone ? (
          <PhonePanel
            tabs={{
              value: activeTab,
              onChange: setActiveTab,
              items: [
                { value: 'active', label: 'Active', count: activeCount, testId: 'active-tab' },
                { value: 'lost', label: 'Lost', count: lostCount, testId: 'lost-tab' },
                { value: 'completed', label: 'Completed', count: completedCount, testId: 'completed-tab' },
              ],
            }}
            actions={[{ label: 'New job', icon: Plus, to: '/jobs/new', primary: true, testId: 'create-job-btn' }]}
          />
        ) : (
        <PageToolbar className="lg:flex-nowrap">
          {/* Left: Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full lg:w-auto lg:shrink-0">
            <TabsList>
              <TabsTrigger value="active" className="flex items-center gap-2" data-testid="active-tab">
                <Briefcase className="w-4 h-4 hidden sm:block" />
                Active <span className="opacity-75 text-sm">{activeCount}</span>
              </TabsTrigger>
              <TabsTrigger value="lost" className="flex items-center gap-2" data-testid="lost-tab">
                <XCircle className="w-4 h-4 hidden sm:block" />
                Lost <span className="opacity-75 text-sm">{lostCount}</span>
              </TabsTrigger>
              <TabsTrigger value="completed" className="flex items-center gap-2" data-testid="completed-tab">
                <CheckCircle className="w-4 h-4 hidden sm:block" />
                Completed <span className="opacity-75 text-sm">{completedCount}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Right: Search & New Job Button */}
          {/* The search gives way first, so the row never wraps beside an open sidebar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:flex-1 lg:min-w-0 lg:justify-end">
            <div className="relative sm:flex-1 min-w-0 lg:min-w-[160px] lg:max-w-[320px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search jobs"
                aria-label="Search jobs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-11 w-full"
                data-testid="job-search-input"
              />
            </div>
            <Button asChild className="shrink-0" data-testid="create-job-btn">
              <Link to="/jobs/new">
                <Plus className="w-[18px] h-[18px]" /> New job
              </Link>
            </Button>
          </div>
        </PageToolbar>
        )}

        {/* Jobs List */}
        {jobsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              {activeTab === 'completed' ? (
                <>
                  <CheckCircle className="w-12 h-12 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No completed jobs</h3>
                  <p className="text-muted-foreground mb-4">
                    {search ? 'Try adjusting your search' : 'Completed jobs will appear here once marked as completed'}
                  </p>
                </>
              ) : activeTab === 'lost' ? (
                <>
                  <XCircle className="w-12 h-12 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No lost jobs</h3>
                  <p className="text-muted-foreground mb-4">
                    {search ? 'Try adjusting your search' : 'Jobs marked as lost will appear here'}
                  </p>
                </>
              ) : (
                <>
                  {search
                    ? <Briefcase className="w-12 h-12 text-muted-foreground/50 mb-4" />
                    : <Illustration name="job" className="w-full max-w-xs mb-2" />}
                  <h3 className={`text-lg font-medium ${search ? 'mb-2' : 'mb-4'}`}>No active jobs</h3>
                  {search && (
                    <p className="text-muted-foreground mb-4">Try adjusting your search</p>
                  )}
                  {!search && (
                    <Button asChild>
                      <Link to="/jobs/new">Create Job</Link>
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : activeTab === 'lost' ? (
          /* Lost jobs - Table view with editable reason */
          <Card className="rounded-cards overflow-hidden" data-testid="lost-jobs-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">Job #</TableHead>
                  <TableHead>Job Name</TableHead>
                  <TableHead className="hidden sm:table-cell">Client</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="min-w-[200px]">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.map((job) => {
                  const grandTotal = job.totals?.grand_total || 
                    ((job.totals?.labour || 0) + (job.totals?.equipment || 0) + (job.totals?.expenses || 0));
                  const isEditingThisRow = editingLostReasonId === job.id;
                  
                  return (
                    <TableRow 
                      key={job.id}
                      className="hover:bg-muted/50"
                      data-testid={`lost-job-row-${job.id}`}
                    >
                      <TableCell 
                        className="pl-4 font-mono text-muted-foreground cursor-pointer"
                        onClick={() => window.location.href = `/jobs/${job.id}`}
                      >
                        {job.job_number || '—'}
                      </TableCell>
                      <TableCell 
                        className="cursor-pointer"
                        onClick={() => window.location.href = `/jobs/${job.id}`}
                      >
                        <p className="font-medium">{job.project_name || 'Untitled Job'}</p>
                      </TableCell>
                      <TableCell 
                        className="hidden sm:table-cell cursor-pointer"
                        onClick={() => window.location.href = `/jobs/${job.id}`}
                      >
                        <span className="text-sm text-muted-foreground">
                          {job.client || '—'}
                        </span>
                      </TableCell>
                      <TableCell 
                        className="text-right font-mono cursor-pointer"
                        onClick={() => window.location.href = `/jobs/${job.id}`}
                      >
                        {formatCurrency(grandTotal)}
                      </TableCell>
                      <TableCell className="pr-4">
                        {isEditingThisRow ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingLostReasonValue}
                              onChange={(e) => setEditingLostReasonValue(e.target.value)}
                              placeholder="Enter reason..."
                              className="h-8 text-sm"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveLostReason(job.id);
                                if (e.key === 'Escape') cancelEditingLostReason();
                              }}
                              data-testid={`lost-reason-edit-input-${job.id}`}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => handleSaveLostReason(job.id)}
                              data-testid={`lost-reason-save-${job.id}`}
                            >
                              <Check className="h-4 w-4 text-success" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={cancelEditingLostReason}
                              data-testid={`lost-reason-cancel-${job.id}`}
                            >
                              <X className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </div>
                        ) : (
                          <div 
                            className="flex items-center gap-2 group cursor-pointer"
                            onClick={() => startEditingLostReason(job.id, job.lost_reason)}
                            data-testid={`lost-reason-display-${job.id}`}
                          >
                            <span className={`text-sm ${job.lost_reason ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                              {job.lost_reason || 'No reason specified'}
                            </span>
                            <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        ) : activeTab === 'completed' ? (
          /* Completed jobs - grouped by financial year & quarter */
          <CompletedJobsBreakdown
            jobs={filteredJobs}
            region={region}
            formatCurrency={formatCurrency}
            taxName={getTaxName()}
          />
        ) : (
          <div className="pt-6">
            <JobKanban
              jobs={filteredJobs}
              formatCurrency={formatCurrency}
              briefsById={briefsById}
              treatmentsByJob={treatmentsByJob}
            />
          </div>
        )}

        {/* Calendar View - Only show on Active tab, hidden on mobile */}
        {activeTab === 'active' && (
          <div className="hidden sm:block">
            <JobCalendarEnhanced jobs={jobs} />
          </div>
        )}
      </div>
    </Layout>
  );
}
